import clsx from 'clsx';
import React from 'react';
import styles from '../RevenueVendorExpenseTable.module.scss';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { infoToolTip } from '../../../../../../assets/hostedassets';
import { CIRCULAR_TXN_KEY, CIRCULAR_TXN_TOOLTIP, NET_COLUMN_TOOLTIP, NET_KEY, TOTAL_COLUMN_KEY } from './overlap.constants';
import SortIcon from '../../../../../../modules/Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import type { SortConfig } from '../../../../../../utils/constants/commonConstants';

interface TableHeaderProps {
    data: { name: string; key?: string }[] | undefined;
    sortConfig?: SortConfig;
    onSort?: (field: string) => void;
}

function Headers(props: TableHeaderProps) {
    const { data, sortConfig, onSort } = props;

    const transformMonthYear = (header: string) => {
        const parts = header.split(' ');
        if (parts.length === 2 && !isNaN(Number(parts[1]))) {
            return `${parts[0]}'${parts[1]}`;
        }
        return header;
    };

    const isMonthYear = (header: string) => {
        const parts = header.split(' ');
        return parts.length === 2 && !isNaN(Number(parts[1]));
    };

    const renderLabel = (column: { name: string; key?: string }) => {
        // Total column (revenue table) — relabelled to "Total" with the shared SortIcon
        // (same component + swap_vert icons the Deals table uses).
        if (column.key === TOTAL_COLUMN_KEY && onSort) {
            return (
                <span className={styles.SortHeaderBtn}>
                    Total
                    <SortIcon field={TOTAL_COLUMN_KEY} sortConfig={sortConfig} onSort={onSort} alwaysVisible />
                </span>
            );
        }
        // Circular Txn. / Net headers carry the explanatory info icon + tooltip.
        const headerTooltip =
            column.key === CIRCULAR_TXN_KEY ? CIRCULAR_TXN_TOOLTIP : column.key === NET_KEY ? NET_COLUMN_TOOLTIP : undefined;
        if (headerTooltip) {
            return (
                <span className={styles.HeaderWithInfo}>
                    {column.name}
                    <Tooltip content={headerTooltip} direction="top" usePortal fitContent>
                        <img src={infoToolTip} alt="info" className={styles.RowInfoIcon} />
                    </Tooltip>
                </span>
            );
        }
        // Month columns are sortable too — same shared SortIcon as the Total column.
        if (isMonthYear(column.name) && onSort && column.key) {
            return (
                <span className={styles.SortHeaderBtn}>
                    {transformMonthYear(column.name)}
                    <SortIcon field={column.key} sortConfig={sortConfig} onSort={onSort} alwaysVisible />
                </span>
            );
        }
        return transformMonthYear(column.name);
    };

    return (
        <tr className={styles.TableHeadRow}>
            {data
                ? data.map((column, i) => (
                      <th
                          key={i}
                          className={clsx(styles.TableHeadRowItem, {
                              [styles.RightAligned]: isMonthYear(column.name),
                              [styles.TotalCol]: column.key === TOTAL_COLUMN_KEY,
                              [styles.CircularCol]: column.key === CIRCULAR_TXN_KEY,
                              [styles.NetCol]: column.key === NET_KEY,
                          })}
                      >
                          {renderLabel(column)}
                      </th>
                  ))
                : null}
        </tr>
    );
}

export default Headers;
